/**
 * Update Accessibility Preferences API
 * Following Bulletproof React two-layer pattern for UPDATE operations
 *
 * Layer 1: Pure mutation function (with upsert logic)
 * Layer 2: React Query mutation hook
 *
 * Upsert Logic:
 * - Checks if accessibility preferences record exists for the user
 * - If exists: Updates existing record with new preferences
 * - If not exists: Creates new record with defaults merged with provided preferences
 *
 * Cache Invalidation:
 * - Invalidates profileQueryKeys.accessibility(userId) on success
 * - This clears the cached accessibility preferences forcing a refetch
 *
 * Usage Examples:
 * ```typescript
 * // Basic usage
 * const { id } = useCurrentUser();
 * const mutation = useUpdateAccessibilityPreferences({ userId: id! });
 *
 * // Trigger update
 * mutation.mutate({
 *   userId: id!,
 *   updates: {
 *     high_contrast_mode: true,
 *     reduced_motion: true,
 *   },
 * });
 *
 * // With custom callbacks
 * const mutation = useUpdateAccessibilityPreferences({
 *   userId: id!,
 *   mutationConfig: {
 *     onSuccess: () => console.log('Saved!'),
 *   },
 * });
 * ```
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MutationConfig } from '@/lib/react-query';
import { profileQueryKeys } from './query-keys';
import {
  DEFAULT_ACCESSIBILITY_PREFERENCES,
  UpdateAccessibilityInput,
} from './types';

// =============================================================================
// LAYER 1: PURE MUTATION FUNCTION
// =============================================================================

/**
 * Layer 1: Pure mutation function with upsert logic
 *
 * Performs an upsert operation on user_preferences_accessibility table:
 * 1. Checks if a record exists for the user
 * 2. If exists: Updates the record with provided preferences
 * 3. If not exists: Creates new record with defaults merged with preferences
 *
 * @param input - The update input containing userId and preferences to update
 * @returns void - No return value needed (cache invalidation handles refresh)
 * @throws Error if database operation fails
 *
 * @example
 * ```typescript
 * await updateAccessibilityPreferences({
 *   userId: '123e4567-e89b-12d3-a456-426614174000',
 *   preferences: {
 *     high_contrast_mode: true,
 *     dyslexia_friendly_font: true,
 *   },
 * });
 * ```
 */
export const updateAccessibilityPreferences = async (
  input: UpdateAccessibilityInput
): Promise<void> => {
  const { userId, preferences } = input;

  // Step 1: Check if accessibility preferences record exists for user
  const { data: existing, error: selectError } = await supabase
    .from('user_preferences_accessibility')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (selectError) {
    console.error('Error checking existing accessibility preferences:', selectError);
    throw selectError;
  }

  if (existing) {
    // Step 2a: Update existing record
    const { error: updateError } = await supabase
      .from('user_preferences_accessibility')
      .update({
        ...preferences,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('Error updating accessibility preferences:', updateError);
      throw updateError;
    }
  } else {
    // Step 2b: Insert new record with defaults merged with preferences
    const { error: insertError } = await supabase
      .from('user_preferences_accessibility')
      .insert({
        user_id: userId,
        ...DEFAULT_ACCESSIBILITY_PREFERENCES,
        ...preferences,
      });

    if (insertError) {
      console.error('Error inserting accessibility preferences:', insertError);
      throw insertError;
    }
  }
};

// =============================================================================
// LAYER 2: REACT QUERY MUTATION HOOK
// =============================================================================

/**
 * Hook options interface for useUpdateAccessibilityPreferences
 */
export interface UseUpdateAccessibilityPreferencesOptions {
  /** User UUID for cache invalidation */
  userId: string;
  /** Optional mutation configuration overrides */
  mutationConfig?: MutationConfig<typeof updateAccessibilityPreferences>;
}

/**
 * Layer 2: React Query mutation hook
 * Provides mutation function with cache invalidation and toast notifications.
 *
 * Features:
 * - Automatic query invalidation on success (profileQueryKeys.accessibility)
 * - Toast notifications for success/error states
 * - Support for custom callbacks via mutationConfig
 * - Uses upsert logic (creates record if doesn't exist, updates if exists)
 *
 * Cache Behavior:
 * - On success: Invalidates profileQueryKeys.accessibility(userId)
 * - This triggers a refetch of accessibility preferences from the database
 *
 * @param options - Hook options including userId and optional mutationConfig
 * @returns React Query mutation result with mutate, isPending, isError, etc.
 *
 * @example
 * ```typescript
 * const { id } = useCurrentUser();
 * const { mutate, isPending } = useUpdateAccessibilityPreferences({
 *   userId: id!,
 * });
 *
 * // Toggle high contrast mode
 * const handleToggleHighContrast = () => {
 *   mutate({
 *     userId: id!,
 *     preferences: {
 *       high_contrast_mode: !currentValue,
 *     },
 *   });
 * };
 *
 * // With custom success callback
 * const mutation = useUpdateAccessibilityPreferences({
 *   userId: id!,
 *   mutationConfig: {
 *     onSuccess: () => {
 *       console.log('Preferences saved!');
 *       applyAccessibilityStyles();
 *     },
 *   },
 * });
 * ```
 */
export const useUpdateAccessibilityPreferences = ({
  userId,
  mutationConfig,
}: UseUpdateAccessibilityPreferencesOptions) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Extract callbacks to handle them separately (Bulletproof React pattern)
  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: updateAccessibilityPreferences,

    // On success: invalidate accessibility cache and show success toast
    onSuccess: (data, variables, context) => {
      // Invalidate user's accessibility preferences to reflect changes
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.accessibility(userId),
      });

      // Show success toast
      toast({
        title: 'Preferences saved',
        description: 'Your accessibility preferences have been updated.',
      });

      // Call custom onSuccess callback if provided
      onSuccess?.(data, variables, context);
    },

    // On error: show error toast
    onError: (error, variables, context) => {
      console.error('Accessibility preferences update failed:', error);
      toast({
        title: 'Save failed',
        description: 'Unable to save preferences. Please try again.',
        variant: 'destructive',
      });

      // Call custom onError callback if provided
      onError?.(error, variables, context);
    },

    // Spread remaining config (excludes callbacks we've already handled)
    ...restConfig,
  });
};
